import {
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { userPool } from '../config/cognito';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { CognitoUser as CognitoUserType, MemberProfile } from '../types/auth';
import {
  AuthenticationError,
  ValidationError,
  NotFoundError,
  logError,
  retryWithBackoff,
  getUserFriendlyMessage,
} from '../utils/errorHandler';
import { API_BASE_URL } from '../config/api';
import { belongsToCurrentOrg, currentOrgId } from '../utils/orgScope';

class AuthService {
  /**
   * Prelink member profile during signup (best-effort).
   */
  async prelinkMember(data: {
    firstName: string;
    lastName: string;
    email: string;
    requestSource?: string;
  }): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/onboarding/prelink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          email: data.email.trim().toLowerCase(),
          requestSource: data.requestSource,
        }),
      });
    } catch (error) {
      logError(error, 'prelinkMember');
    }
  }
  /**
   * Register a new member after Cognito signup + email verification.
   * Creates member doc + profileLink on the backend.
   */
  async register(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  }): Promise<{ status: string; memberId: string; verificationStatus: string }> {
    const token = localStorage.getItem('idToken');
    const response = await fetch(`${API_BASE_URL}/api/onboarding/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Registration failed');
    }

    return response.json();
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<CognitoUserType> {
    try {
      // Validate inputs
      if (!email || !password) {
        throw new ValidationError('Email and password are required');
      }

      return await new Promise((resolve, reject) => {
        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session: CognitoUserSession) => {
            try {
              const idToken = session.getIdToken();
              const payload = idToken.payload;

              const user: CognitoUserType = {
                userId: payload.sub,
                email: payload.email,
                given_name: payload.given_name,
                family_name: payload.family_name,
                email_verified: payload.email_verified || false,
                // Include custom role attributes
                'custom:roles': payload['custom:roles'],
                'custom:custom:roles': payload['custom:custom:roles'],
              };

              // Store token in localStorage for API requests
              localStorage.setItem('idToken', idToken.getJwtToken());
              localStorage.setItem('refreshToken', session.getRefreshToken().getToken());

              resolve(user);
            } catch (error) {
              logError(error, 'signIn - onSuccess');
              reject(new AuthenticationError('Failed to process authentication'));
            }
          },
          onFailure: err => {
            logError(err, 'signIn - onFailure');
            reject(new AuthenticationError(getUserFriendlyMessage(err)));
          },
          newPasswordRequired: () => {
            reject(new AuthenticationError('New password required'));
          },
        });
      });
    } catch (error) {
      logError(error, 'signIn');
      throw error instanceof AuthenticationError || error instanceof ValidationError
        ? error
        : new AuthenticationError(getUserFriendlyMessage(error));
    }
  }

  /**
   * Sign up new user
   */
  async signUp(
    email: string,
    password: string,
    attributes: {
      given_name: string;
      family_name: string;
    },
  ): Promise<ISignUpResult> {
    try {
      // Validate inputs
      if (!email || !password) {
        throw new ValidationError('Email and password are required');
      }
      if (!attributes.given_name || !attributes.family_name) {
        throw new ValidationError('First name and last name are required');
      }

      return await new Promise((resolve, reject) => {
        const attributeList = [
          new CognitoUserAttribute({ Name: 'email', Value: email }),
          new CognitoUserAttribute({ Name: 'given_name', Value: attributes.given_name }),
          new CognitoUserAttribute({ Name: 'family_name', Value: attributes.family_name }),
          new CognitoUserAttribute({ Name: 'custom:organizationId', Value: currentOrgId() }),
        ];

        userPool.signUp(email, password, attributeList, [], (err, result) => {
          if (err) {
            logError(err, 'signUp');
            reject(new AuthenticationError(getUserFriendlyMessage(err)));
            return;
          }
          if (!result) {
            reject(new AuthenticationError('Sign up failed'));
            return;
          }
          resolve(result);
        });
      });
    } catch (error) {
      logError(error, 'signUp');
      throw error instanceof AuthenticationError || error instanceof ValidationError
        ? error
        : new AuthenticationError(getUserFriendlyMessage(error));
    }
  }

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, err => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode(err => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<CognitoUserType | null> {
    return new Promise(resolve => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const idToken = session.getIdToken();
        const payload = idToken.payload;

        const user: CognitoUserType = {
          userId: payload.sub,
          email: payload.email,
          given_name: payload.given_name,
          family_name: payload.family_name,
          email_verified: payload.email_verified || false,
          // Include custom role attributes
          'custom:roles': payload['custom:roles'],
          'custom:custom:roles': payload['custom:custom:roles'],
        };

        // Update stored token
        localStorage.setItem('idToken', idToken.getJwtToken());

        resolve(user);
      });
    });
  }

  /**
   * Get current session token
   */
  async getIdToken(): Promise<string | null> {
    return new Promise(resolve => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        resolve(session.getIdToken().getJwtToken());
      });
    });
  }

  /**
   * Refresh session token
   */
  async refreshSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No session found'));
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
          if (refreshErr) {
            reject(refreshErr);
            return;
          }

          // Update stored tokens
          localStorage.setItem('idToken', newSession.getIdToken().getJwtToken());
          localStorage.setItem('refreshToken', newSession.getRefreshToken().getToken());

          resolve();
        });
      });
    });
  }

  /**
   * Change password
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No session found'));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, changeErr => {
          if (changeErr) {
            reject(changeErr);
            return;
          }
          resolve();
        });
      });
    });
  }

  /**
   * Forgot password - send reset code
   */
  async forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: err => {
          reject(err);
        },
      });
    });
  }

  /**
   * Confirm password reset with code
   */
  async confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: err => {
          reject(err);
        },
      });
    });
  }

  /**
   * Get member profile linked to Cognito user
   * Checks both profile links (mobile app style) and member documents (member web style) for compatibility
   */
  async getMemberProfile(cognitoUserId: string): Promise<MemberProfile | null> {
    try {
      if (!cognitoUserId) {
        throw new ValidationError('User ID is required');
      }

      // Backend-first: the /api/mobile/profile endpoint resolves the member with
      // the admin SDK and the org from the verified token — it's fast (~300ms),
      // reliable, and not subject to client Firestore rules / org-from-hostname.
      // The client read below is kept only as a fallback. (Previously this was
      // client-first, which on some tenants always failed and burned ~7-9s of
      // retryWithBackoff retries per call — twice per signup — before falling
      // back here, making the first home load take ~20s.)
      const backendProfile = await this.getMemberProfileFromBackend();
      if (backendProfile) return backendProfile;

      // Fallback: direct client Firestore read. One quick retry only — if it
      // can't surface the member, there's nothing faster to wait for.
      const clientProfile = await retryWithBackoff(async () => {
        let memberId: string | null = null;
        let memberData: any = null;
        let needsFirebaseUserIdUpdate = false;

        // First, check for profile link (mobile app style)
        try {
          const { profileLinkService } = await import('./profileLinkService');
          const profileLink = await profileLinkService.getProfileLink(cognitoUserId);
          
          if (profileLink) {
            console.log('🔗 AuthService: Found profile link, loading member profile');
            memberId = profileLink.memberId;
            
            // Load the actual member profile
            const memberRef = doc(db, 'members', memberId);
            const memberSnap = await getDoc(memberRef);

            if (memberSnap.exists() && belongsToCurrentOrg(memberSnap.data())) {
              memberData = memberSnap.data();

              // Check if firebaseUserId needs to be set
              if (!memberData.firebaseUserId) {
                needsFirebaseUserIdUpdate = true;
              }
            }
          }
        } catch (profileLinkError) {
          console.warn('⚠️ AuthService: Error checking profile link:', profileLinkError);
          // Continue to fallback methods
        }

        // Fallback: try to find member by document ID (cognitoUserId)
        if (!memberId) {
          const memberRef = doc(db, 'members', cognitoUserId);
          const memberSnap = await getDoc(memberRef);

          if (memberSnap.exists() && belongsToCurrentOrg(memberSnap.data())) {
            memberId = memberSnap.id;
            memberData = memberSnap.data();
            
            // Check if firebaseUserId needs to be set (for PaymentService lookup)
            if (!memberData.firebaseUserId) {
              needsFirebaseUserIdUpdate = true;
            }
          }
        }

        // Fallback: try to find by cognitoUserId field
        if (!memberId) {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const membersRef = collection(db, 'members');
          const q = query(membersRef, where('cognitoUserId', '==', cognitoUserId));
          const querySnapshot = await getDocs(q);

          const orgDocs = querySnapshot.docs.filter(d => belongsToCurrentOrg(d.data()));
          if (orgDocs.length > 0) {
            const doc = orgDocs[0];
            memberId = doc.id;
            memberData = doc.data();
            
            // Check if firebaseUserId needs to be set
            if (!memberData.firebaseUserId) {
              needsFirebaseUserIdUpdate = true;
            }
          }
        }

        if (!memberId || !memberData) {
          console.log('⚠️ AuthService: No member profile found for user:', cognitoUserId);
          return null;
        }

        // Update member document with firebaseUserId if needed (for PaymentService)
        if (needsFirebaseUserIdUpdate) {
          console.log(`🔧 AuthService: Updating member ${memberId} with firebaseUserId for payment lookups`);
          try {
            const memberDocRef = doc(db, 'members', memberId);
            await updateDoc(memberDocRef, {
              firebaseUserId: cognitoUserId, // PaymentService looks for this field
              firebaseUid: cognitoUserId, // Keep for backward compatibility
              cognitoUserId: cognitoUserId, // Keep for consistency
              lastLoginAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
            console.log(`✅ AuthService: Member document updated with firebaseUserId`);
          } catch (updateError) {
            console.error('❌ AuthService: Failed to update member with firebaseUserId:', updateError);
            // Continue anyway - this is not critical for sign-in
          }
        }

        // Check if entityMemberships already exists on the document
        let entityMemberships = [];
        
        if (memberData.entityMemberships && Array.isArray(memberData.entityMemberships)) {
          // Entity memberships are already on the document
          entityMemberships = memberData.entityMemberships.map((membership: any) => ({
            entityId: membership.entityId,
            entityName: membership.entityName || '',
            entityType: membership.entityType || 'chapter',
            role: Array.isArray(membership.roles) ? membership.roles[0] : (membership.role || 'member'),
            status: membership.status || 'active',
            joinedAt: membership.joinedAt?.toDate() || new Date(),
          }));
          console.log(`🔍 AuthService: Found ${entityMemberships.length} entity memberships on member document`);
        } else {
          // Load from member_associations collection
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const associationsRef = collection(db, 'member_associations');
          const associationsQuery = query(
            associationsRef,
            where('memberId', '==', memberId),
            where('status', '==', 'active')
          );
          const associationsSnapshot = await getDocs(associationsQuery);

          entityMemberships = associationsSnapshot.docs.filter(doc => belongsToCurrentOrg(doc.data())).map(doc => {
            const assocData = doc.data();
            return {
              entityId: assocData.entityId,
              entityName: assocData.entityName || '',
              entityType: assocData.entityType || 'chapter',
              role: assocData.role || 'member',
              status: assocData.status || 'active',
              joinedAt: assocData.startDate?.toDate() || new Date(),
            };
          });
        }

        // Build display name from available fields
        const displayName = memberData.displayName || 
                           memberData.fullName || 
                           `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim() ||
                           memberData.email?.split('@')[0] || 
                           'Member';

        // Get profile image from various possible field names
        const profileImageUrl = memberData.profileImageUrl || 
                               memberData.photoUrl || 
                               memberData.profilePicture || 
                               memberData.avatar || 
                               '';

        return {
          id: memberId,
          firstName: memberData.firstName || '',
          lastName: memberData.lastName || '',
          displayName,
          email: memberData.email || '',
          phone: memberData.phone || '',
          profileImageUrl,
          initiationDate: memberData.initiationDate?.toDate ? memberData.initiationDate.toDate() : memberData.initiationDate ? new Date(memberData.initiationDate) : undefined,
          status: memberData.status || 'active',
          allowDirectMessages: memberData.allowDirectMessages ?? true,
          hiddenThreads: memberData.hiddenThreads || [],
          verificationStatus: memberData.verificationStatus || undefined,
          entityMemberships,
          createdAt: memberData.createdAt?.toDate() || new Date(),
          updatedAt: memberData.updatedAt?.toDate() || new Date(),
        };
      }, 1, 300);

      // Backend already returned null above, so the client read is the last
      // word — whatever it found (a profile or null) is the result.
      return clientProfile;
    } catch (error: any) {
      // A brand-new user (e.g. just-invited, not yet linked to a member record)
      // isn't authorized to read their profile yet, so Firestore rules reject
      // with permission-denied. The backend was already tried first, so just
      // treat these as "no profile" rather than surfacing an error.
      const code = error?.code;
      if (
        code === 'permission-denied' ||
        code === 'firestore/permission-denied' ||
        code === 'unauthenticated' ||
        code === 'not-found'
      ) {
        console.warn('getMemberProfile: client fallback read blocked:', code);
        return null;
      }
      logError(error, 'getMemberProfile');
      throw new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Load the member profile from the backend (admin SDK, org from the verified
   * token). Used as a fallback when the client-side Firestore read can't surface
   * the member — e.g. a just-invited member whose doc/link the security rules
   * won't return to a client query yet. Returns null if there's still no member.
   */
  private async getMemberProfileFromBackend(): Promise<MemberProfile | null> {
    try {
      const token = localStorage.getItem('idToken');
      if (!token) return null;
      const resp = await fetch(`${API_BASE_URL}/api/mobile/profile`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-organization-id': currentOrgId(),
        },
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      const p = data?.profile;
      if (!p || !p.id) return null;

      const entityMemberships = (p.entityMemberships || []).map((m: any) => ({
        entityId: m.entityId,
        entityName: m.entityName || '',
        entityType: m.entityType || 'chapter',
        role: Array.isArray(m.roles) ? m.roles[0] : m.role || 'member',
        status: m.status || 'active',
        joinedAt: m.startDate
          ? new Date(m.startDate)
          : m.joinedAt
            ? new Date(m.joinedAt)
            : new Date(),
      }));

      const displayName =
        p.displayName ||
        `${p.firstName || ''} ${p.lastName || ''}`.trim() ||
        p.email?.split('@')[0] ||
        'Member';

      return {
        id: p.id,
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        displayName,
        email: p.email || '',
        phone: p.phone || p.phoneNumber || '',
        profileImageUrl: p.profilePicture || p.profileImageUrl || '',
        status: p.status || 'active',
        allowDirectMessages: p.allowDirectMessages ?? true,
        hiddenThreads: p.hiddenThreads || [],
        verificationStatus: p.verificationStatus || undefined,
        entityMemberships,
        createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
        updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
      };
    } catch (err) {
      console.warn('getMemberProfileFromBackend failed:', err);
      return null;
    }
  }

  /**
   * Claim/link member profile to Cognito user
   * Uses ProfileLinkService for consistency with mobile app
   */
  async claimProfile(cognitoUserId: string, memberId: string): Promise<void> {
    try {
      if (!cognitoUserId || !memberId) {
        throw new ValidationError('User ID and member ID are required');
      }

      // Import ProfileLinkService dynamically to avoid circular dependencies
      const { profileLinkService } = await import('./profileLinkService');
      
      // Create profile link using the same approach as mobile app
      await profileLinkService.createProfileLink(cognitoUserId, memberId);
      
      console.log('✅ AuthService: Profile link created successfully');
    } catch (error) {
      logError(error, 'claimProfile');
      throw new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Create a new member profile for a Cognito user
   */
  async createMemberProfile(
    cognitoUserId: string,
    profileData: Partial<MemberProfile>,
  ): Promise<MemberProfile> {
    try {
      if (!cognitoUserId) {
        throw new ValidationError('User ID is required');
      }
      if (!profileData.email) {
        throw new ValidationError('Email is required');
      }

      return await retryWithBackoff(async () => {
        const memberRef = doc(db, 'members', cognitoUserId);
        const now = Timestamp.now();

        const newProfile = {
          cognitoUserId,
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          displayName: profileData.displayName || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          profileImageUrl: profileData.profileImageUrl || '',
          status: profileData.status || 'active',
          allowDirectMessages: true,
          hiddenThreads: [],
          createdAt: now,
          updatedAt: now,
        };

        await setDoc(memberRef, newProfile);

        return {
          id: cognitoUserId,
          ...newProfile,
          createdAt: now.toDate(),
          updatedAt: now.toDate(),
        } as MemberProfile;
      });
    } catch (error) {
      logError(error, 'createMemberProfile');
      throw new Error(getUserFriendlyMessage(error));
    }
  }
}

export const authService = new AuthService();
export default authService;
